import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { X, Plus, Trash2 } from "lucide-react";

export default function ScorecardTemplateForm({ template, onSubmit, onClose, isLoading }) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    categories: [{ category: "", weight: 1, description: "" }],
    is_active: true,
  });

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || "",
        description: template.description || "",
        categories: template.categories || [{ category: "", weight: 1, description: "" }],
        is_active: template.is_active ?? true,
      });
    }
  }, [template]);

  const addCategory = () => {
    setFormData({
      ...formData,
      categories: [...formData.categories, { category: "", weight: 1, description: "" }],
    });
  };

  const removeCategory = (index) => {
    setFormData({
      ...formData,
      categories: formData.categories.filter((_, i) => i !== index),
    });
  };

  const updateCategory = (index, field, value) => {
    const updated = [...formData.categories];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, categories: updated });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-xl font-semibold text-slate-900">
            {template ? "Rediger Scorecard-mal" : "Ny Scorecard-mal"}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name">Navn på mal *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="f.eks. Standard Oppkjøpsevaluering"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Beskrivelse</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              placeholder="Beskrivelse av når denne malen skal brukes"
            />
          </div>

          <div className="border-t border-slate-200 pt-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">Evalueringskategorier</h3>
              <Button type="button" onClick={addCategory} size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Legg til kategori
              </Button>
            </div>

            <div className="space-y-4">
              {formData.categories.map((cat, index) => (
                <div key={index} className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Kategori *</Label>
                          <Input
                            value={cat.category}
                            onChange={(e) => updateCategory(index, "category", e.target.value)}
                            required
                            placeholder="f.eks. Markedsposisjon"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Vekt (1-5) *</Label>
                          <Input
                            type="number"
                            min="1"
                            max="5"
                            value={cat.weight}
                            onChange={(e) => updateCategory(index, "weight", parseFloat(e.target.value) || 1)}
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Beskrivelse</Label>
                        <Textarea
                          value={cat.description}
                          onChange={(e) => updateCategory(index, "description", e.target.value)}
                          rows={2}
                          placeholder="Hva evalueres i denne kategorien?"
                        />
                      </div>
                    </div>
                    {formData.categories.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeCategory(index)}
                        className="text-rose-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Avbryt</Button>
            <Button type="submit" disabled={isLoading} className="bg-slate-900 hover:bg-slate-800">
              {isLoading ? "Lagrer..." : template ? "Oppdater" : "Opprett"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}